import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MangaNavbar from "@/components/feature/MangaNavbar";
import MangaFooter from "@/components/feature/MangaFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/MangaBreadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { comics } from "@/mocks/comics";

type Tab = "热度榜" | "评分榜" | "收藏榜";

const tabs: Tab[] = ["热度榜", "评分榜", "收藏榜"];

export default function Ranking() {
  useSeo({
    title: "人气榜单 - 热门连载漫画排行 | 天天漫画网",
    description:
      "天天漫画网人气榜单，汇总热门日漫、国漫的热度榜、评分榜与收藏榜。看看大家都在追哪些人气连载漫画，追更不迷路。",
    keywords: "人气漫画榜单,漫画排行,热门连载漫画,追更排行榜,漫画每日更新",
    canonicalPath: "/ranking",
  });

  const [tab, setTab] = useState<Tab>("热度榜");

  const list = useMemo(() => {
    const arr = [...comics];
    if (tab === "评分榜") return arr.sort((a, b) => b.rating - a.rating);
    if (tab === "收藏榜") return arr.sort((a, b) => Number(b.viewCount.replace("万", "")) - Number(a.viewCount.replace("万", "")));
    return arr.sort((a, b) => b.heat - a.heat);
  }, [tab]);

  const breadcrumbItems = [{ label: "首页", to: "/" }, { label: "人气榜单" }];

  const itemListData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `人气榜单 - ${tab}`,
    url: "https://panelpeek.github.io/ranking",
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.slice(0, 8).map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.title,
        url: `https://panelpeek.github.io/comic/${c.id}`,
      })),
    },
  };

  const medal = (idx: number) =>
    idx === 0 ? "bg-accent-500" : idx === 1 ? "bg-secondary-500" : idx === 2 ? "bg-primary-500" : "bg-background-200 text-foreground-600";

  return (
    <>
      <JsonLd id="ranking-jsonld" data={itemListData} />
      <MangaNavbar />
      <Breadcrumb items={breadcrumbItems} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">人气榜单</h1>
            <p className="mt-2 text-sm md:text-base text-foreground-500">从不同维度看看大家都在追的热门连载漫画。</p>
          </div>

          <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100 border border-background-200 self-start md:self-auto">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  tab === t ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {list.map((comic, idx) => (
            <article
              key={comic.id}
              className="group flex items-center gap-4 md:gap-6 rounded-2xl bg-background-50 border border-background-200 p-3 md:p-4 hover:border-primary-200 transition-colors"
            >
              <span
                className={`shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center font-heading text-lg ${medal(idx)} ${
                  idx < 3 ? "text-background-50" : ""
                }`}
              >
                {idx + 1}
              </span>

              <Link to={`/comic/${comic.id}`} className="shrink-0 w-16 sm:w-24 md:w-28 aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 人气漫画第${idx + 1}名`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </Link>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base md:text-lg font-medium text-foreground-900 truncate">
                    <Link to={`/comic/${comic.id}`} className="hover:text-primary-600 transition-colors">
                      {comic.title}
                    </Link>
                  </h2>
                  <span className="text-[11px] text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-md">{comic.genre}</span>
                </div>
                <p className="mt-1 hidden md:block text-sm text-foreground-500 truncate">{comic.description}</p>
                <div className="mt-2 flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-flashlight-fill text-primary-500"></i>{comic.latestChapter}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-eye-line text-foreground-400"></i>{comic.viewCount} 人气
                  </span>
                </div>
              </div>

              <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
                <span className="flex items-center gap-1 text-sm font-medium text-accent-600">
                  <i className="ri-star-fill text-accent-500"></i>{comic.rating}
                </span>
                <Link
                  to={`/comic/${comic.id}`}
                  className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
                >
                  去追更 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </main>
      <MangaFooter />
      <BackToTop />
    </>
  );
}